import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../api/axiosConfig';
import './ManagementPages.css';
import './ValidationPage.css';

// Avis des consommateurs recueillis par les hôtesses. Un avis = un consommateur ; il ne
// contient que les marques dont il a parlé. Le responsable ne voit que sa base (filtré par
// le serveur). Rubriques et critères identiques à l'écran mobile AvisConsommateurScreen.

const RUBRIQUES = [
  {
    cle: 'EAUX', titre: 'Eaux',
    marques: [['SUPERMONT', 'Supermont'], ['SUPERMONT PLATE', 'Supermont la plate'],
      ['SUPERMONT PETILLANTE', 'Supermont la pétillante'], ['OPUR', 'Opur']],
    // Gaz : seulement pour Supermont la pétillante, les autres eaux affichent « — »
    criteres: [['GOUT', 'Goût'], ['ODEUR', 'Odeur'], ['LIMPIDITE', 'Couleur / limpidité'], ['GAZ', 'Gaz']],
  },
  {
    cle: 'BOISSONS_GAZEUSES', titre: 'Boissons gazeuses',
    marques: [['PLANET', 'Planet'], ['AMERICAN COLA', 'American Cola'], ['BUBBLE UP', 'Bubble Up']],
    criteres: [['GOUT', 'Goût'], ['ODEUR', 'Odeur'], ['GAZ', 'Gaz'], ['SUCRE', 'Sucre']],
  },
  {
    cle: 'BOISSONS_ENERGISANTES', titre: 'Boissons énergisantes',
    marques: [['REAKTOR', 'Reaktor']],
    criteres: [['GOUT', 'Goût'], ['ODEUR', 'Odeur'], ['EFFET', 'Effet / énergie'], ['SUCRE', 'Sucre']],
  },
];

const NOMS_MARQUES = Object.fromEntries(RUBRIQUES.flatMap(r => r.marques));
const NOMS_CRITERES = Object.fromEntries(RUBRIQUES.flatMap(r => r.criteres));

const jourIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const ilYa = (jours) => { const d = new Date(); d.setDate(d.getDate() - jours); return jourIso(d); };
// Heure du téléphone de l'hôtesse, lue telle quelle
const dateHeure = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}` : '—');

function AvisConsommateursPage() {
  const estAdmin = (localStorage.getItem('userRole') || '').toLowerCase() === 'administrateur';
  const [avis, setAvis] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [du, setDu] = useState(ilYa(6));
  const [au, setAu] = useState(jourIso(new Date()));
  const [base, setBase] = useState('');
  const [marqueFiltre, setMarqueFiltre] = useState('');

  const charger = useCallback(async () => {
    setIsLoading(true);
    setErreur(null);
    try {
      const params = new URLSearchParams({ date_debut: du, date_fin: au });
      if (estAdmin && base) params.append('base', base);
      const reponse = await axiosInstance.get(`/hotesses/avis?${params.toString()}`);
      setAvis(Array.isArray(reponse.data) ? reponse.data : []);
    } catch (e) {
      setErreur(e.response?.data?.detail || 'Impossible de charger les avis. Réessayez dans un instant.');
    } finally {
      setIsLoading(false);
    }
  }, [du, au, base, estAdmin]);

  useEffect(() => { charger(); }, [charger]);

  // Pour chaque marque : nombre de consommateurs qui en ont parlé, et 👍/👎 par critère
  const resume = useMemo(() => {
    const r = {};
    avis.forEach(a => a.marques.forEach(m => {
      const x = r[m.marque] || (r[m.marque] = { clients: 0, criteres: {} });
      x.clients += 1;
      m.criteres.forEach(c => {
        const y = x.criteres[c.critere] || (x.criteres[c.critere] = { POSITIF: 0, NEGATIF: 0 });
        y[c.sens] = (y[c.sens] || 0) + 1;
      });
    }));
    return r;
  }, [avis]);

  const avisAffiches = marqueFiltre ? avis.filter(a => a.marques.some(m => m.marque === marqueFiltre)) : avis;
  const nbHotesses = new Set(avis.map(a => a.hotesse_id)).size;

  const champ = { padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' };
  const libelle = { fontSize: '12px', fontWeight: '600', color: '#495057' };
  const colonne = { display: 'flex', flexDirection: 'column', gap: '4px' };
  const compteur = (valeur, texte, couleur) => (
    <div style={{ background: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '12px 18px', minWidth: '140px' }}>
      <div style={{ fontSize: '26px', fontWeight: 700, color: couleur }}>{valeur}</div>
      <div style={{ fontSize: '13px', color: '#6c757d' }}>{texte}</div>
    </div>
  );
  const cellule = (v) => {
    if (!v || (!v.POSITIF && !v.NEGATIF)) return <span style={{ color: '#ced4da' }}>—</span>;
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        <span style={{ color: '#28a745', fontWeight: 600 }}>👍 {v.POSITIF || 0}</span>
        {'  '}
        <span style={{ color: '#dc3545', fontWeight: 600 }}>👎 {v.NEGATIF || 0}</span>
      </span>
    );
  };

  return (
    <div className="management-page" style={{ padding: '20px' }}>
      <h1 style={{ color: '#2c3e50', margin: 0 }}>Avis des consommateurs</h1>
      <p style={{ color: '#6c757d', marginTop: '6px' }}>
        Ce que les clients des supermarchés disent de nos marques, noté par les hôtesses. Un avis = un client.
      </p>

      <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <div style={colonne}>
            <label style={libelle}>Du</label>
            <input type="date" style={champ} value={du} onChange={(e) => setDu(e.target.value || ilYa(6))} />
          </div>
          <div style={colonne}>
            <label style={libelle}>Au</label>
            <input type="date" style={champ} value={au} onChange={(e) => setAu(e.target.value || jourIso(new Date()))} />
          </div>
          {estAdmin && (
            <div style={colonne}>
              <label style={libelle}>Base</label>
              <select style={{ ...champ, minWidth: '140px' }} value={base} onChange={(e) => setBase(e.target.value)}>
                <option value="">Toutes les bases</option>
                <option value="DOUALA">DOUALA</option>
                <option value="YAOUNDE">YAOUNDE</option>
                <option value="BAFOUSSAM">BAFOUSSAM</option>
              </select>
            </div>
          )}
          <button onClick={charger}
            style={{ padding: '7px 18px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
            Actualiser
          </button>
        </div>
      </div>

      {erreur && <p className="no-data-message" style={{ color: '#dc3545' }}>{erreur}</p>}

      {isLoading ? (
        <p style={{ textAlign: 'center', padding: '40px' }}>Chargement...</p>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
            {compteur(avis.length, 'clients interrogés', '#2c3e50')}
            {compteur(nbHotesses, 'hôtesses', '#0d6efd')}
          </div>

          {RUBRIQUES.map(r => (
            <div key={r.cle} className="management-table-container" style={{ marginBottom: '20px' }}>
              <h2 style={{ color: '#0d6efd', fontSize: '18px', margin: '0 0 10px' }}>{r.titre}</h2>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Marque</th>
                    <th>Clients</th>
                    {r.criteres.map(([cle, nom]) => <th key={cle}>{nom}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {r.marques.map(([cle, nom]) => {
                    const x = resume[cle];
                    return (
                      <tr key={cle}>
                        <td><strong>{nom}</strong></td>
                        <td>{x?.clients || 0}</td>
                        {r.criteres.map(([c]) => <td key={c}>{cellule(x?.criteres[c])}</td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}

          <div className="management-table-container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <h2 style={{ color: '#2c3e50', fontSize: '18px', margin: 0 }}>Détail des avis</h2>
              <select style={champ} value={marqueFiltre} onChange={(e) => setMarqueFiltre(e.target.value)}>
                <option value="">Toutes les marques</option>
                {Object.entries(NOMS_MARQUES).map(([cle, nom]) => <option key={cle} value={cle}>{nom}</option>)}
              </select>
            </div>
            {avisAffiches.length === 0 ? (
              <p className="no-data-message">Aucun avis sur cette période.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Hôtesse</th>
                    <th>Supermarché</th>
                    <th>Avis</th>
                  </tr>
                </thead>
                <tbody>
                  {avisAffiches.map(a => (
                    <tr key={a.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{dateHeure(a.heure_saisie)}</td>
                      <td>{a.hotesse_nom}{estAdmin && a.base ? <div style={{ fontSize: '12px', color: '#6c757d' }}>{a.base}</div> : null}</td>
                      <td>{a.client_nom || '—'}</td>
                      <td>
                        {a.marques
                          .filter(m => !marqueFiltre || m.marque === marqueFiltre)
                          .map(m => (
                            <div key={m.marque} style={{ marginBottom: '6px' }}>
                              <strong>{NOMS_MARQUES[m.marque] || m.marque}</strong>
                              {m.criteres.map(c => (
                                <span key={c.critere} style={{
                                  display: 'inline-block', marginLeft: '6px', padding: '1px 8px', borderRadius: '10px', fontSize: '12px',
                                  background: c.sens === 'POSITIF' ? '#e6f4ea' : '#fdecea',
                                  color: c.sens === 'POSITIF' ? '#1e7e34' : '#c62828',
                                }}>
                                  {NOMS_CRITERES[c.critere] || c.critere} {c.sens === 'POSITIF' ? '👍' : '👎'}
                                </span>
                              ))}
                              {m.commentaire && (
                                <div style={{ fontSize: '13px', color: '#495057', fontStyle: 'italic', marginTop: '2px' }}>« {m.commentaire} »</div>
                              )}
                            </div>
                          ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AvisConsommateursPage;
